import { useState, useEffect, useCallback } from 'react';

/**
 * Auto-Save Hook for Calculator
 * Automatically saves calculator state to localStorage
 */
export const useAutoSave = (key, initialData, delay = 2000) => {
  const [data, setData] = useState(() => {
    // Try to load from localStorage on mount
    if (typeof window === 'undefined') return initialData;
    
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if saved data is recent (within 24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed.data;
        }
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
    return initialData;
  });

  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null);

  // Save to localStorage
  const saveData = useCallback((dataToSave) => {
    try {
      setSaveStatus('saving');
      const toSave = {
        data: dataToSave,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(toSave));
      setSaveStatus('saved');
      setLastSaved(new Date());
      
      // Reset status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving data:', error);
      setSaveStatus('error');
    }
  }, [key]);

  // Auto-save effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (data && Object.keys(data).length > 0) {
        saveData(data);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [data, delay, saveData]);

  // Clear saved data
  const clearSaved = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setData(initialData);
      setSaveStatus('idle');
      setLastSaved(null);
    } catch (error) {
      console.error('Error clearing saved data:', error);
    }
  }, [key, initialData]);

  // Check if there's saved data
  const hasSavedData = useCallback(() => {
    try {
      const saved = localStorage.getItem(key);
      return !!saved;
    } catch {
      return false;
    }
  }, [key]);

  return {
    data,
    setData,
    saveStatus,
    lastSaved,
    clearSaved,
    hasSavedData,
    manualSave: () => saveData(data)
  };
};

/**
 * Auto-Save Status Indicator Component
 */
export const AutoSaveIndicator = ({ saveStatus, lastSaved }) => {
  if (saveStatus === 'idle' && !lastSaved) return null;

  const statusConfig = {
    saving: {
      text: 'Saving draft...',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200'
    },
    saved: {
      text: 'Draft saved',
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    },
    error: {
      text: 'Save failed',
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200'
    }
  };

  const config = statusConfig[saveStatus] || statusConfig.saved;

  return (
    <div className={`fixed bottom-4 right-4 ${config.bg} ${config.border} border rounded-lg px-4 py-2 shadow-lg transition-all z-50`}>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${config.color} animate-pulse`}></div>
        <span className={`text-sm font-medium ${config.color}`}>
          {config.text}
          {lastSaved && saveStatus === 'saved' && (
            <span className="text-xs ml-2 opacity-75">
              {new Date(lastSaved).toLocaleTimeString()}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};
